import * as React from "react";
import { Box, Container, Typography, Grid, Card, Stack, Button, Chip } from "@mui/material";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import EmailIcon from "@mui/icons-material/Email";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

// Image imports
import contactImage1 from "../../assets/images/C5C_5551.jpg";
import contactImage2 from "../../assets/images/C5C_5520.jpg";
import contactImage3 from "../../assets/images/C5C_5514.jpg";

const cards = [
  {
    icon: <LocationOnIcon />,
    title: "Our Address",
    value: "246/6B, Kumarasinghe Road, Badulla",
    helper: "Visit our main office for service information and travel support.",
  },
  {
    icon: <LocalPhoneIcon />,
    title: "Call Us",
    value: "+94 766333030 | +94 775333030",
    helper: "Use our hotline for trip details, seat availability, and booking help.",
  },
  {
    icon: <EmailIcon />,
    title: "Email Us",
    value: "info@vishmitha.lk",
    helper: "Send questions, service requests, or feedback anytime.",
  },
];

export default function Contact() {
  return (
    <Box id="contact" sx={{ bgcolor: "#F4F7FB", pt: 10, pb: 10 }}>
      <Box
        sx={{
          background: "linear-gradient(150deg, #0D1B2A 0%, #1565C0 60%, #0288D1 100%)",
          px: 2,
          py: { xs: 7, md: 9 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                icon={<SupportAgentIcon sx={{ color: "inherit !important" }} />}
                label="Contact Vishmitha Highway Express"
                sx={{ mb: 2, bgcolor: "rgba(255,255,255,0.14)", color: "white", fontWeight: 700 }}
              />
              <Typography variant="h2" sx={{ color: "white", fontWeight: 900, fontSize: { xs: "2.2rem", md: "3.4rem" }, lineHeight: 1.05, mb: 2 }}>
                Reach the team quickly when you need route or booking support.
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.75)", maxWidth: 620, lineHeight: 1.8 }}>
                Whether you need travel details, help with seat selection, or answers about a booking, our contact channels are kept simple and direct.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }}>
                <Button variant="contained" href="tel:+94766333030" sx={{ borderRadius: 99, px: 3, py: 1.2, fontWeight: 700, bgcolor: "white", color: "#1565C0", boxShadow: "none", '&:hover': { bgcolor: '#E3F2FD', boxShadow: 'none' } }}>
                  Call Now
                </Button>
                <Button variant="outlined" href="mailto:info@vishmitha.lk" sx={{ borderRadius: 99, px: 3, py: 1.2, fontWeight: 700, borderColor: "rgba(255,255,255,0.34)", color: "white", '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.06)' } }}>
                  Send Email
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {[contactImage1, contactImage2, contactImage3].map((image, index) => (
                  <Grid item xs={12} sm={index === 0 ? 12 : 6} key={index}>
                    <Card sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 20px 60px rgba(8,20,40,0.18)" }}>
                      <Box
                        sx={{
                          height: index === 0 ? { xs: 220, md: 260 } : 180,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          backgroundImage: `url(${image})`,
                        }}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: "relative", zIndex: 1 }}>
        <Grid container spacing={2.5}>
          {cards.map((card) => (
            <Grid item xs={12} md={4} key={card.title}>
              <Card sx={{ borderRadius: 3, p: 3, height: "100%", border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 16px 36px rgba(13,27,42,0.08)" }}>
                <Box sx={{ width: 52, height: 52, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "#E3F2FD", color: "#1565C0", mb: 2 }}>
                  {card.icon}
                </Box>
                <Typography variant="h6" fontWeight={800} color="#0D1B2A" gutterBottom>
                  {card.title}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: "#1565C0", mb: 1 }}>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {card.helper}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 7, p: { xs: 3, md: 4 }, borderRadius: 4, bgcolor: "white", border: "1px solid rgba(0,0,0,0.06)" }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ color: "#1565C0", fontWeight: 800, letterSpacing: 1.2 }}>
                Customer Support
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 900, color: "#0D1B2A", mb: 1.5 }}>
                Talk to the right team faster.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.9 }}>
                Use phone support for urgent trip questions, email for booking follow-up and service feedback, and the office address for direct contact when needed. The page is designed to make those options immediately visible instead of burying them below decorative content.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack spacing={1.25}>
                <Chip label="Route inquiries" sx={{ justifyContent: "flex-start", bgcolor: "#F4F7FB" }} />
                <Chip label="Seat booking support" sx={{ justifyContent: "flex-start", bgcolor: "#F4F7FB" }} />
                <Chip label="Travel updates" sx={{ justifyContent: "flex-start", bgcolor: "#F4F7FB" }} />
                <Chip label="Customer feedback" sx={{ justifyContent: "flex-start", bgcolor: "#F4F7FB" }} />
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
